import { createIntl, createIntlCache } from 'react-intl';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import enUSMessages from '@/locales/en-US';
import zhCNMessages from '@/locales/zh-CN';

export const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const;
export const APP_LOCALE_STORAGE_KEY = 'umi_locale';

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

const intlCache = createIntlCache();

const intlMap = {
  'zh-CN': createIntl({ locale: 'zh-CN', messages: zhCNMessages }, intlCache),
  'en-US': createIntl({ locale: 'en-US', messages: enUSMessages }, intlCache),
} as const;

export const normalizeAppLocale = (locale?: string | null): AppLocale => {
  const normalized = String(locale || '').toLowerCase();
  return normalized.startsWith('en') ? 'en-US' : 'zh-CN';
};

const readStoredLocale = (): string | null => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(APP_LOCALE_STORAGE_KEY);
  } catch (_error) {
    return null;
  }
};

const readBrowserLocale = (): string | null => {
  if (typeof navigator === 'undefined' || typeof navigator.language !== 'string') {
    return null;
  }

  return navigator.language;
};

export const getRawAppLocale = (): string | null => readStoredLocale() || readBrowserLocale();

export const getAppLocale = (): AppLocale => normalizeAppLocale(getRawAppLocale());

export const ensureAppLocale = (): AppLocale => {
  const locale = getAppLocale();

  if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
    try {
      if (window.localStorage.getItem(APP_LOCALE_STORAGE_KEY) !== locale) {
        window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
      }
    } catch (_error) {
      return locale;
    }
  }

  return locale;
};

export const isZhCNLocale = (locale?: string | null) => normalizeAppLocale(locale) === 'zh-CN';

export const getAntdLocale = (locale?: string | null) =>
  isZhCNLocale(locale ?? getAppLocale()) ? zhCN : enUS;

export const formatLocaleMessage = (
  id: string,
  defaultMessage: string,
  values?: Record<string, any>,
) => intlMap[getAppLocale()].formatMessage({ id, defaultMessage }, values);
